import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { NavigateFunction } from "react-router-dom";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { GetFaxStatusResponse } from "../../../data-model/faxStatusModel";
import { logError } from "../../../utils/customLogger";

/**
 * Handles the submission of a fax document by sending it through the fax service
 * and navigating to the submission status page.
 *
 * This function orchestrates the fax submission workflow:
 * 1. Validates that required parameters (faxPdfLink and formId) are present
 * 2. Sets the submission state to indicate processing
 * 3. Calls the fax service to send the document and update Firestore
 * 4. Navigates to the submission status page on success
 * 5. Displays an error toast and logs errors if submission fails
 *
 * @param {string} id - The unique identifier for the worklist item
 * @param {string} faxPdfLink - The URL/path to the PDF document to be faxed
 * @param {string} formId - The unique identifier for the form being submitted
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setIsSubmitting - State setter to control the submission loading state
 * @param {NavigateFunction} navigate - React Router navigation function for redirecting after submission
 * @param {Function} makeSendCallAndUpdateFirestore - Async function that sends the fax and updates Firestore
 * @param {string} makeSendCallAndUpdateFirestore.faxPdfLink - The PDF link parameter for the send call
 * @param {string} makeSendCallAndUpdateFirestore.formId - The form ID parameter for the send call
 * @returns {Promise<void>} Resolves when the fax submission process completes (success or failure)
 *
 * @throws {Error} Logs errors but does not throw - errors are caught and displayed via toast notification
 *
 * @example
 * ```typescript
 * await handleFaxSubmission(
 *   "worklist-123",
 *   "https://example.com/document.pdf",
 *   "form-456",
 *   setIsSubmitting,
 *   navigate,
 *   makeSendCallAndUpdateFirestore
 * );
 * ```
 */
export const handleFaxSubmission = async (
  id: string,
  faxPdfLink: string,
  formId: string,
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  navigate: NavigateFunction,
  makeSendCallAndUpdateFirestore: (
    faxPdfLink: string,
    formId: string,
  ) => Promise<GetFaxStatusResponse | undefined>,
) => {
  if (!faxPdfLink || !formId) {
    return;
  }
  try {
    setIsSubmitting(true);
    await makeSendCallAndUpdateFirestore(faxPdfLink, formId);
    navigate(`/worklists/submission-status/${id}`, {
      state: { isFaxSubmitted: true },
    });
  } catch (error) {
    logError(error as Error, "Error handling fax submission");
    toast.error(ToastMessages.FAX_SUBMISSION_FAILURE);
    return;
  } finally {
    setIsSubmitting(false);
  }
};
